import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import clsx from 'clsx';
import { formatInTimeZone } from 'date-fns-tz';

import { CustomDialog } from 'components/Dialogs/CustomDialog';
import { CustomFormControl } from 'components/Form/FormControl';
import ButtonWithArrowIcon from 'components/Buttons/ButtonWithArrowIcon';
import { IDocument } from 'common/document/interfaces/document';
import { DocumentTypeEnum } from 'common/document/enums/document-type.enum';
import { DocumentRequestEventNameEnum } from 'common/event/enums/document-request-event-name.enum';
import { originalSuffix } from 'common/document/constants/original-suffix';
import { formatDate } from 'functions/format/formatDate';
import { getTicketId } from 'functions/ticket';
import { DriverLicensePdf } from '../DocumentPdf/DriverLicensePdf';
import { DOTMedicalCardPdf } from '../DocumentPdf/DOTMedicalCardPdf';
import { getUrl, prepareFiles } from './utils';
import { ReactComponent as CheckmarkIcon } from 'assets/images/input-checkmark.svg';

import styles from './styles.module.scss';
import { DriverChecklistPdf } from '../DocumentPdf/DriverChecklistPdf';
import { needToUseSsn } from '../../../../functions/needToUseSsn';
import { GenericDocumentPdf } from '../DocumentPdf/GenericDocumentPdf';
import { TextButton } from '../../../../components/Buttons/TextButton';
import Loader from '../../../../components/Loaders/Loader';
import { downloadFile } from '../../../../functions/file/downloadFile';

type DocImages = {
  firstUrl?: string;
  secondUrl?: string;
};

type InputProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  driverName: string;
  employeeId: string;
  document?: IDocument;
  photoIdDocument?: IDocument;
  onDownload?: (document: IDocument) => void;
  saveDocumentPdfToServer?: (
    candidateDocumentId: string,
    filename?: string,
    callbackSuccess?: () => void
  ) => void;
  isLoadingSaveDocumentPdfToServer?: boolean;
};

export const DocumentDownloadDialog = ({
  open,
  setOpen,
  driverName,
  employeeId,
  document,
  photoIdDocument,
  onDownload,
  saveDocumentPdfToServer,
  isLoadingSaveDocumentPdfToServer,
}: InputProps) => {
  const [snn, setSnn] = useState(needToUseSsn() ? '' : employeeId || '');
  const [sendToServerBtn, setSendToServerBtn] = useState('Send to Server');
  const [filename, setFilename] = useState('');
  const [dateOnboarded, setSateOnboarded] = useState('');
  const [imageUrlList, setImageUrlList] = useState<DocImages>({
    firstUrl: undefined,
    secondUrl: undefined,
  });
  const [photoIdDocumentImage, setPhotoIdDocumentImage] = useState<
    string | undefined
  >();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let prefix = '';

    switch (document?.documentType) {
      case DocumentTypeEnum.DRIVER_LICENSE:
        prefix = 'LD - ';
        break;
      case DocumentTypeEnum.DOT_MEDICAL:
        prefix = 'MC - ';
        break;
      case DocumentTypeEnum.DRIVER_CHECKLIST:
        prefix = 'MED_CHECKLIST - ';
        break;
      case DocumentTypeEnum.RETURN_TO_WORK:
        prefix = 'RETURN_TO_WORK - ';
        break;
      case DocumentTypeEnum.MEDICAL_DISCONTINUANCE:
        prefix = 'MED_DISCONTINUANCE - ';
        break;
    }
    setSnn(needToUseSsn() ? '' : employeeId || '');
    setFilename(
      `${prefix}${driverName.replace(/[^a-zA-Z0-9\s]/g, '').replace(' ', ', ')}`
    );
  }, [document, driverName]);

  useEffect(() => {
    const approved = document?.documentEvents.find(
      (event) =>
        event.eventName === DocumentRequestEventNameEnum.DOCUMENT_APPROVED
    );
    if (approved?.eventDate) {
      setSateOnboarded(
        formatDate(new Date(Date.parse(approved.eventDate)), 'MM/dd/yy')
      );
    } else {
      setSateOnboarded('');
    }
  }, [document]);

  const docFiles = useMemo(() => {
    if (document?.documentType === DocumentTypeEnum.DRIVER_CHECKLIST) {
      return [];
    }
    return (
      document?.documentFiles.filter(
        (docFile) => !docFile.documentName.endsWith(originalSuffix)
      ) || []
    );
  }, [document]);

  useEffect(() => {
    prepareFiles({
      first: docFiles[0]?.fileUrl,
      second: docFiles[1]?.fileUrl,
    })
      .then((urls) => {
        setImageUrlList({ ...urls });
        setIsLoading(false);
      })
      .catch((error) => console.error(error));
  }, [docFiles]);

  const photoIdDocumentFiles = useMemo(() => {
    return (
      photoIdDocument?.documentFiles.filter((docFile) =>
        docFile.documentName.endsWith('file')
      ) || []
    );
  }, [photoIdDocument]);

  useEffect(() => {
    if (!photoIdDocumentFiles?.length) return;

    getUrl(photoIdDocumentFiles[0]?.fileUrl)
      .then((url) => {
        setPhotoIdDocumentImage(url);
        setIsLoading(false);
      })
      .catch((error) => console.error(error));
  }, [photoIdDocumentFiles]);

  const handleChangeSnn = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const v = event.target.value.toString();
    setSnn(v);
  };

  const handleChangeFilename = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const v = event.target.value.toString();
    setFilename(v);
  };

  const approvedEvent = useMemo(() => {
    return document?.documentEvents?.find(
      (event) =>
        event.eventName !== DocumentRequestEventNameEnum.DOCUMENT_APPROVED
    );
  }, [document]);

  const dotVerificationScreenshotDate = useMemo(() => {
    return (
      document?.verifications?.['DOT_MEDICAL_VERIFICATION_IMAGE']?.updatedAt ||
      null
    );
  }, [document]);

  const handleDownload = () => {
    if (document) {
      onDownload && onDownload(document);
    }
  };

  const handleSaveDocumentPdfToServer = () => {
    if (document) {
      saveDocumentPdfToServer &&
        saveDocumentPdfToServer(document.candidateDocumentId, filename, () => {
          setSendToServerBtn('Sent to Server');
        });
    }
  };

  const errorMessageInFileName = useMemo(() => {
    if (!filename) {
      return 'field is required';
    }
    const invalidChar = new RegExp(/[^a-zA-Z0-9\-_ ,]/, 'img');
    if (invalidChar.test(filename)) {
      return 'field has invalid character. Use: letters, numbers, comma, or -';
    }
    return '';
  }, [filename]);

  const isLoadingFiles = useMemo(() => {
    if (
      document?.documentType &&
      document.documentType === DocumentTypeEnum.DRIVER_CHECKLIST
    ) {
      return false;
    }

    return (
      typeof imageUrlList?.firstUrl === 'undefined' &&
      typeof imageUrlList?.secondUrl === 'undefined'
    );
  }, [imageUrlList, document?.documentType]);

  const isPdf = useMemo(() => {
    return !!docFiles?.length && docFiles[0].fileType === 'application/pdf';
  }, [docFiles]);

  const handleDownloadPdfFile = useCallback(async () => {
    if (isPdf && document?.documentFiles[0]?.fileUrl) {
      await downloadFile({
        fileUrl: document?.documentFiles[0]?.fileUrl,
        filename,
      });
    }
  }, [isPdf, filename, document?.documentFiles[0]?.fileUrl]);

  return (
    <CustomDialog open={open} setOpen={setOpen} title="Download PDF">
      {isLoadingSaveDocumentPdfToServer ? (
        <Loader />
      ) : (
        <div className={styles.root}>
          <>
            <div className={styles.actions}>
              <CustomFormControl
                label={needToUseSsn() ? 'Driver’s SSN' : 'Alpha Code'}
                inputProps={{
                  value: snn,
                  onChange: handleChangeSnn,
                  disabled: true,
                }}
              />
              <CustomFormControl
                label={'File Name'}
                error={errorMessageInFileName}
                errorMessage={errorMessageInFileName}
                inputProps={{
                  value: filename,
                  onChange: handleChangeFilename,
                  endAdornment: (
                    <CheckmarkIcon
                      style={{ position: 'relative', top: '2px' }}
                    />
                  ),
                }}
              />
            </div>
            <div className={clsx(styles.actions)}>
              <div className={styles.textButtonWrapper}>
                {errorMessageInFileName ? (
                  <TextButton
                    disabled={true}
                    className={styles.textButton}
                    data-test-id="download-pdf"
                  >
                    Download PDF
                  </TextButton>
                ) : isPdf ? (
                  <TextButton
                    className={styles.textButton}
                    data-test-id="download-pdf"
                    onClick={handleDownloadPdfFile}
                  >
                    Download PDF
                  </TextButton>
                ) : (
                  <PDFDownloadLink
                    style={{
                      textDecoration: 'none',
                      color: '#011627',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                    document={
                      document?.documentType ===
                      DocumentTypeEnum.DOT_MEDICAL ? (
                        <DOTMedicalCardPdf
                          snn={snn}
                          driverName={driverName}
                          employeeId={employeeId}
                          screenshotCreatedAt={
                            dotVerificationScreenshotDate
                              ? formatInTimeZone(
                                  dotVerificationScreenshotDate,
                                  'America/Chicago',
                                  'MM/dd/yyyy HH:mm zzz'
                                )
                              : ''
                          }
                          approvedAt={
                            approvedEvent?.eventDate
                              ? formatInTimeZone(
                                  approvedEvent.eventDate,
                                  'America/Chicago',
                                  'MM/dd/yyyy HH:mm zzz'
                                )
                              : ''
                          }
                          ticketId={getTicketId(document?.ticketId || '')}
                          dateOnboarded={dateOnboarded}
                          firstImage={imageUrlList.firstUrl}
                          secondImage={imageUrlList.secondUrl}
                        />
                      ) : document?.documentType ===
                        DocumentTypeEnum.DRIVER_LICENSE ? (
                        <DriverLicensePdf
                          snn={snn}
                          driverName={driverName}
                          employeeId={employeeId}
                          ticketId={getTicketId(document?.ticketId || '')}
                          dateOnboarded={dateOnboarded}
                          firstImage={imageUrlList.firstUrl}
                          secondImage={imageUrlList.secondUrl}
                          photoIdDocumentImage={photoIdDocumentImage}
                        />
                      ) : document?.documentType ===
                        DocumentTypeEnum.DRIVER_CHECKLIST ? (
                        <DriverChecklistPdf
                          logo={'/images/JB-Hunt.png'}
                          driverName={driverName}
                          employeeId={
                            needToUseSsn() ? employeeId : snn || employeeId
                          }
                          date={
                            approvedEvent?.eventDate
                              ? formatInTimeZone(
                                  approvedEvent.eventDate,
                                  'America/Chicago',
                                  'MM/dd/yyyy'
                                )
                              : ''
                          }
                          details={document?.details}
                        />
                      ) : (
                        <GenericDocumentPdf
                          snn={snn}
                          driverName={driverName}
                          employeeId={employeeId}
                          approvedAt={
                            approvedEvent?.eventDate
                              ? formatInTimeZone(
                                  approvedEvent.eventDate,
                                  'America/Chicago',
                                  'MM/dd/yyyy HH:mm zzz'
                                )
                              : ''
                          }
                          ticketId={getTicketId(document?.ticketId || '')}
                          dateOnboarded={dateOnboarded}
                          firstImage={imageUrlList.firstUrl}
                        />
                      )
                    }
                    fileName={filename}
                  >
                    {({ loading, error }) => (
                      <>
                        {loading || isLoading || isLoadingFiles ? (
                          <TextButton disabled={true}>Loading...</TextButton>
                        ) : (
                          <TextButton
                            disabled={
                              !!errorMessageInFileName || isLoadingFiles
                            }
                            onClick={handleDownload}
                            className={styles.textButton}
                            data-test-id="download-pdf"
                          >
                            Download PDF
                          </TextButton>
                        )}
                        {error}
                      </>
                    )}
                  </PDFDownloadLink>
                )}
              </div>
              <div>
                <ButtonWithArrowIcon
                  disabled={!!errorMessageInFileName}
                  downArrow={true}
                  onClick={handleSaveDocumentPdfToServer}
                  data-test-id="send-to-server"
                >
                  {sendToServerBtn}
                </ButtonWithArrowIcon>
              </div>
            </div>
          </>
        </div>
      )}
    </CustomDialog>
  );
};
